import { lazyRouteComponent, notFound } from "@tanstack/react-router";

import LandingNotFound from "@/components/landing/LandingNotFound";
import {
  CONTENT_CACHE_CONTROL,
  CONTENT_NOT_FOUND_CACHE_CONTROL,
} from "@/content/cachePolicy";
import type { LegalPage, LegalSlug } from "@/content/legal";
import { legalPageContent } from "./legalSource";

/**
 * Everything one of the ten legal and company routes is.
 *
 * Each route file is three lines: the framework needs a module per URL, and
 * `createFileRoute` needs its path as a literal so the generated route tree is
 * typed. Nothing else lives there, no component, no copy, no head. The page is
 * a list of blocks in the CMS and `LegalDocument` renders it, so a new page is
 * a document in the Studio, a run of `scripts/refresh-sanity-index.mjs` to put
 * its slug in `LEGAL_SLUGS`, and a file that says the same three lines as the
 * others, as `/master-services-agreement` was added.
 *
 * There is no committed registry behind the loader, deliberately: see
 * `./legalSource.ts` and `content/cmsRegistry.ts`. A slug the CMS does not
 * carry answers a real 404 rather than a page saying so, which is what keeps a
 * withdrawn document out of the index.
 *
 * A dynamic `/$legalSlug` would have removed even the ten files, and was
 * tried: the dashboard already claims `/$planId` at the root, so a second
 * one-segment parameter route makes every such URL ambiguous, and the router
 * resolves `/privacy-policy` to `$planId` and warns. Static paths outrank a
 * parameter, so these ten are unambiguous and the dashboard's not-found splat
 * keeps every URL that is not one of them.
 *
 * Sibling of `_app`, not a child, for the reason `blog`, `academy`, `home` and
 * `pricing` are: these pages are public, and `_app`'s guard would bounce an
 * anonymous reader to sign-in.
 *
 * The component is imported rather than written here. The router plugin splits
 * a route's component into its own chunk by reading the `component` property
 * out of a route file, and it can see neither this indirection nor a function
 * expression inside an object literal, so the whole template rode into the
 * entry chunk behind this module. `lazyRouteComponent` states the boundary the
 * plugin could not infer, and the router awaits it before it renders, on the
 * server and on the client alike.
 */
export function legalRoute(slug: LegalSlug) {
  return {
    loader: async (): Promise<LegalPage> => {
      const page = await legalPageContent({ data: slug });
      if (!page) {
        throw notFound();
      }
      return page;
    },
    /** `loaderData` is absent exactly when the loader threw `notFound()`. */
    headers: ({ loaderData }: { loaderData?: LegalPage }) => ({
      "Cache-Control": loaderData
        ? CONTENT_CACHE_CONTROL
        : CONTENT_NOT_FOUND_CACHE_CONTROL,
    }),
    component: lazyRouteComponent(() =>
      import("./LegalDocumentRoute").then(({ legalDocument }) => ({
        default: legalDocument(slug),
      }))
    ),
    notFoundComponent: LandingNotFound,
  };
}
